(() => {
  'use strict';

  const componentCleanups = new Map();
  const reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  const prefersReducedMotion = () => Boolean(reducedMotionQuery && reducedMotionQuery.matches);

  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

  const elementsWithin = (scope, selector) => {
    const elements = [];

    if (scope instanceof Element && scope.matches(selector)) {
      elements.push(scope);
    }

    if (scope && typeof scope.querySelectorAll === 'function') {
      elements.push(...scope.querySelectorAll(selector));
    }

    return elements;
  };

  const focusWithoutJump = (element) => {
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const retargetLegacyHeroLink = () => {
    const storyTarget = document.querySelector('.hush-ultra-evolved');
    const legacyLink = document.querySelector(
      '.section-hush-home .hush-home__hero a[href^="#HushFormula-"]'
    );

    if (!storyTarget || !legacyLink) {
      return;
    }

    if (!storyTarget.id) {
      storyTarget.id = 'HushUltraStory';
    }

    legacyLink.setAttribute('href', `#${storyTarget.id}`);
  };

  const initializeCarousel = (carousel) => {
    const viewport = carousel.querySelector('[data-hush-carousel-viewport]');
    const slides = Array.from(carousel.querySelectorAll('[data-hush-carousel-slide]')).filter(
      (slide) => slide.closest('[data-hush-carousel]') === carousel
    );
    const previousButton = carousel.querySelector('[data-hush-carousel-prev]');
    const nextButton = carousel.querySelector('[data-hush-carousel-next]');

    if (!viewport || slides.length === 0) {
      return null;
    }

    let frameRequest = null;
    let resizeObserver = null;

    const isRightToLeft = () => window.getComputedStyle(viewport).direction === 'rtl';

    const leadingEdgeDistance = (slide) => {
      const viewportRect = viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();

      return isRightToLeft()
        ? Math.abs(viewportRect.right - slideRect.right)
        : Math.abs(viewportRect.left - slideRect.left);
    };

    const currentSlideIndex = () => {
      let currentIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const distance = leadingEdgeDistance(slide);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          currentIndex = index;
        }
      });

      return currentIndex;
    };

    const isAtStart = () => {
      const viewportRect = viewport.getBoundingClientRect();
      const firstSlideRect = slides[0].getBoundingClientRect();
      const tolerance = 3;

      if (isRightToLeft()) {
        return (
          firstSlideRect.right <= viewportRect.right + tolerance &&
          firstSlideRect.right > viewportRect.left + tolerance
        );
      }

      return (
        firstSlideRect.left >= viewportRect.left - tolerance &&
        firstSlideRect.left < viewportRect.right - tolerance
      );
    };

    const isAtEnd = () => {
      const viewportRect = viewport.getBoundingClientRect();
      const lastSlideRect = slides[slides.length - 1].getBoundingClientRect();
      const tolerance = 3;

      if (isRightToLeft()) {
        return (
          lastSlideRect.left >= viewportRect.left - tolerance &&
          lastSlideRect.left < viewportRect.right - tolerance
        );
      }

      return (
        lastSlideRect.right <= viewportRect.right + tolerance &&
        lastSlideRect.right > viewportRect.left + tolerance
      );
    };

    const setButtonState = (button, disabled) => {
      if (!button) {
        return;
      }

      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    };

    const updateCarouselState = () => {
      frameRequest = null;

      const hasOverflow = viewport.scrollWidth > viewport.clientWidth + 3;
      const activeIndex = currentSlideIndex();

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.dataset.hushCarouselActive = String(isActive);
      });

      setButtonState(previousButton, !hasOverflow || isAtStart());
      setButtonState(nextButton, !hasOverflow || isAtEnd());
    };

    const requestStateUpdate = () => {
      if (frameRequest !== null) {
        return;
      }

      frameRequest = window.requestAnimationFrame(updateCarouselState);
    };

    const scrollToSlide = (index) => {
      const boundedIndex = Math.max(0, Math.min(index, slides.length - 1));
      const targetSlide = slides[boundedIndex];

      if (!targetSlide) {
        return;
      }

      targetSlide.scrollIntoView({
        behavior: scrollBehavior(),
        block: 'nearest',
        inline: 'start'
      });

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === boundedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.dataset.hushCarouselActive = String(isActive);
      });

      window.setTimeout(requestStateUpdate, prefersReducedMotion() ? 0 : 350);
    };

    const moveCarousel = (direction) => {
      if (carousel.dataset.hushCarouselStep === 'card') {
        scrollToSlide(currentSlideIndex() + direction);
        return;
      }

      const directionMultiplier = isRightToLeft() ? -1 : 1;
      viewport.scrollBy({
        left: viewport.clientWidth * 0.9 * direction * directionMultiplier,
        behavior: scrollBehavior()
      });
    };

    const handlePreviousClick = () => moveCarousel(-1);
    const handleNextClick = () => moveCarousel(1);

    const handleViewportKeydown = (event) => {
      if (event.target !== viewport || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      let targetIndex = null;

      switch (event.key) {
        case 'ArrowLeft':
          targetIndex = currentSlideIndex() + (isRightToLeft() ? 1 : -1);
          break;
        case 'ArrowRight':
          targetIndex = currentSlideIndex() + (isRightToLeft() ? -1 : 1);
          break;
        case 'Home':
          targetIndex = 0;
          break;
        case 'End':
          targetIndex = slides.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      scrollToSlide(targetIndex);
    };

    const handleImageLoad = (event) => {
      if (event.target instanceof HTMLImageElement) {
        requestStateUpdate();
      }
    };

    viewport.addEventListener('scroll', requestStateUpdate, { passive: true });
    viewport.addEventListener('keydown', handleViewportKeydown);
    carousel.addEventListener('load', handleImageLoad, true);
    previousButton?.addEventListener('click', handlePreviousClick);
    nextButton?.addEventListener('click', handleNextClick);

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestStateUpdate);
      resizeObserver.observe(viewport);
      slides.forEach((slide) => resizeObserver.observe(slide));
    } else {
      window.addEventListener('resize', requestStateUpdate, { passive: true });
    }

    carousel.dataset.hushCarouselReady = 'true';
    requestStateUpdate();

    return () => {
      viewport.removeEventListener('scroll', requestStateUpdate);
      viewport.removeEventListener('keydown', handleViewportKeydown);
      carousel.removeEventListener('load', handleImageLoad, true);
      previousButton?.removeEventListener('click', handlePreviousClick);
      nextButton?.removeEventListener('click', handleNextClick);
      resizeObserver?.disconnect();

      if (!resizeObserver) {
        window.removeEventListener('resize', requestStateUpdate);
      }

      if (frameRequest !== null) {
        window.cancelAnimationFrame(frameRequest);
      }

      delete carousel.dataset.hushCarouselReady;
    };
  };

  const initializeTabs = (tabsRoot) => {
    const tablist = tabsRoot.querySelector('[role="tablist"]');
    const tabs = Array.from(tabsRoot.querySelectorAll('[data-hush-ultra-tab]')).filter(
      (tab) => tab.closest('[data-hush-ultra-tabs]') === tabsRoot
    );
    const panels = Array.from(tabsRoot.querySelectorAll('[data-hush-ultra-tab-panel]')).filter(
      (panel) => panel.closest('[data-hush-ultra-tabs]') === tabsRoot
    );

    if (!tablist || tabs.length === 0 || panels.length === 0) {
      return null;
    }

    const panelsByCategory = new Map(
      panels.map((panel) => [panel.dataset.hushUltraTabPanel, panel])
    );

    const activateTab = (tab, moveFocus = false) => {
      if (!tab || !tabs.includes(tab)) {
        return;
      }

      const category = tab.dataset.hushUltraTab;
      const activePanel = panelsByCategory.get(category);

      if (!activePanel) {
        return;
      }

      tabs.forEach((candidate) => {
        const isActive = candidate === tab;
        candidate.setAttribute('aria-selected', String(isActive));
        candidate.tabIndex = isActive ? 0 : -1;
        candidate.classList.toggle('is-active', isActive);
      });

      panels.forEach((panel) => {
        const isActive = panel === activePanel;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      if (moveFocus) {
        focusWithoutJump(tab);
      }
    };

    const handleTabClick = (event) => {
      const tab = event.target.closest('[data-hush-ultra-tab]');

      if (!tab || !tabs.includes(tab)) {
        return;
      }

      activateTab(tab);
    };

    const handleTabKeydown = (event) => {
      const currentTab = event.target.closest('[data-hush-ultra-tab]');

      if (
        !currentTab ||
        !tabs.includes(currentTab) ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      const currentIndex = tabs.indexOf(currentTab);
      const vertical = tablist.getAttribute('aria-orientation') === 'vertical';
      const rightToLeft = window.getComputedStyle(tablist).direction === 'rtl';
      let nextIndex = null;

      switch (event.key) {
        case 'ArrowLeft':
          if (!vertical) {
            nextIndex = currentIndex + (rightToLeft ? 1 : -1);
          }
          break;
        case 'ArrowRight':
          if (!vertical) {
            nextIndex = currentIndex + (rightToLeft ? -1 : 1);
          }
          break;
        case 'ArrowUp':
          if (vertical) {
            nextIndex = currentIndex - 1;
          }
          break;
        case 'ArrowDown':
          if (vertical) {
            nextIndex = currentIndex + 1;
          }
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex === null) {
        return;
      }

      event.preventDefault();
      const wrappedIndex = (nextIndex + tabs.length) % tabs.length;
      activateTab(tabs[wrappedIndex], true);
    };

    const handleBlockSelect = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const selectedPanel = event.target.closest('[data-hush-ultra-tab-panel]');

      if (!selectedPanel || !panels.includes(selectedPanel)) {
        return;
      }

      const selectedTab = tabs.find(
        (tab) => tab.dataset.hushUltraTab === selectedPanel.dataset.hushUltraTabPanel
      );

      activateTab(selectedTab);
    };

    tablist.addEventListener('click', handleTabClick);
    tablist.addEventListener('keydown', handleTabKeydown);
    tabsRoot.addEventListener('shopify:block:select', handleBlockSelect);

    const initiallySelectedTab =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    activateTab(initiallySelectedTab);
    tabsRoot.dataset.hushUltraTabsReady = 'true';

    return () => {
      tablist.removeEventListener('click', handleTabClick);
      tablist.removeEventListener('keydown', handleTabKeydown);
      tabsRoot.removeEventListener('shopify:block:select', handleBlockSelect);
      delete tabsRoot.dataset.hushUltraTabsReady;
    };
  };

  const initializeWorld = (world) => {
    const stages = Array.from(world.querySelectorAll('[data-hush-ultra-stage]')).filter(
      (stage) => stage.closest('[data-hush-ultra-world]') === world
    );
    const triggers = Array.from(world.querySelectorAll('[data-hush-ultra-stage-trigger]')).filter(
      (trigger) => trigger.closest('[data-hush-ultra-world]') === world
    );

    if (stages.length === 0) {
      return null;
    }

    let intersectionObserver = null;
    let fallbackFrameRequest = null;
    const intersectingStages = new Set();

    const stageByIndex = new Map(
      stages.map((stage, index) => [stage.dataset.hushUltraStageIndex || String(index), stage])
    );

    const activateStage = (stage) => {
      if (!stage || !stages.includes(stage)) {
        return;
      }

      const activeIndex = stage.dataset.hushUltraStageIndex;

      stages.forEach((candidate) => {
        const isActive = candidate === stage;
        candidate.classList.toggle('is-active', isActive);
        candidate.dataset.hushUltraActive = String(isActive);
      });

      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.hushUltraStageIndex === activeIndex;
        trigger.classList.toggle('is-active', isActive);

        if (isActive) {
          trigger.setAttribute('aria-current', 'step');
        } else {
          trigger.removeAttribute('aria-current');
        }
      });
    };

    const updateStageFromViewport = () => {
      fallbackFrameRequest = null;
      const worldRect = world.getBoundingClientRect();

      if (worldRect.bottom <= 0 || worldRect.top >= window.innerHeight) {
        return;
      }

      const viewportTarget = window.innerHeight * 0.5;
      const candidates = intersectingStages.size > 0 ? Array.from(intersectingStages) : stages;
      let closestStage = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      candidates.forEach((stage) => {
        const stageRect = stage.getBoundingClientRect();
        const stageCenter = stageRect.top + stageRect.height / 2;
        const distance = Math.abs(stageCenter - viewportTarget);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestStage = stage;
        }
      });

      activateStage(closestStage);
    };

    const requestStageUpdate = () => {
      if (fallbackFrameRequest !== null) {
        return;
      }

      fallbackFrameRequest = window.requestAnimationFrame(updateStageFromViewport);
    };

    const handleTriggerClick = (event) => {
      const trigger = event.currentTarget;
      const targetStage = stageByIndex.get(trigger.dataset.hushUltraStageIndex);

      if (!targetStage || event.defaultPrevented) {
        return;
      }

      if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      event.preventDefault();
      activateStage(targetStage);
      targetStage.scrollIntoView({
        behavior: scrollBehavior(),
        block: 'center',
        inline: 'nearest'
      });
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', handleTriggerClick));

    if ('IntersectionObserver' in window) {
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              intersectingStages.add(entry.target);
            } else {
              intersectingStages.delete(entry.target);
            }
          });

          requestStageUpdate();
        },
        {
          root: null,
          rootMargin: '-35% 0px -45% 0px',
          threshold: [0, 0.01, 0.25, 0.5, 0.75, 1]
        }
      );

      stages.forEach((stage) => intersectionObserver.observe(stage));
    } else {
      window.addEventListener('scroll', requestStageUpdate, { passive: true });
      window.addEventListener('resize', requestStageUpdate, { passive: true });
    }

    const initiallyActiveStage =
      stages.find((stage) => stage.dataset.hushUltraActive === 'true') || stages[0];
    activateStage(initiallyActiveStage);
    world.dataset.hushUltraWorldReady = 'true';
    requestStageUpdate();

    return () => {
      triggers.forEach((trigger) => trigger.removeEventListener('click', handleTriggerClick));
      intersectionObserver?.disconnect();

      if (!intersectionObserver) {
        window.removeEventListener('scroll', requestStageUpdate);
        window.removeEventListener('resize', requestStageUpdate);
      }

      if (fallbackFrameRequest !== null) {
        window.cancelAnimationFrame(fallbackFrameRequest);
      }

      delete world.dataset.hushUltraWorldReady;
    };
  };

  const initializeComponents = (scope = document) => {
    retargetLegacyHeroLink();

    const componentTypes = [
      ['[data-hush-carousel]', initializeCarousel],
      ['[data-hush-ultra-tabs]', initializeTabs],
      ['[data-hush-ultra-world]', initializeWorld]
    ];

    componentTypes.forEach(([selector, initializer]) => {
      elementsWithin(scope, selector).forEach((element) => {
        if (componentCleanups.has(element)) {
          return;
        }

        const cleanup = initializer(element);

        if (typeof cleanup === 'function') {
          componentCleanups.set(element, cleanup);
        }
      });
    });
  };

  const destroyComponents = (scope) => {
    const selectors = '[data-hush-carousel], [data-hush-ultra-tabs], [data-hush-ultra-world]';

    elementsWithin(scope, selectors).forEach((element) => {
      const cleanup = componentCleanups.get(element);

      if (cleanup) {
        cleanup();
        componentCleanups.delete(element);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeComponents(document), { once: true });
  } else {
    initializeComponents(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeComponents(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyComponents(event.target);
  });
})();
